// Core packages
import {
  Component, Inject, OnDestroy, OnInit
} from "@angular/core";

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { DdtsService } from "src/app/modules/ddts/ddts.service";
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { UsersService } from "src/app/modules/users/users.service";
import { PrintQrDialogComponent } from "src/app/shared/components/dialogs/print-qr-dialog/print-qr-dialog.component";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";

/**
 * Script start
 */

@Component({
  selector: "app-property-detail-document-print-module-dialog",
  templateUrl: "./document-print-module-dialog.component.html",
  styleUrls: ["./document-print-module-dialog.component.scss"],
})
export class PropertyDetailDocumentPrintModuleDialogComponent
  implements OnInit, OnDestroy
{
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  dialogIsLoading = true; // Used to view/hide form loading spinner

  printModuleBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.printModule"),
    spinnerSize: 19,
    raised: false,
    stroked: false,
    flat: true,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-print",
      inline: true,
    },
  };
  printQrBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.printLabel"),
    spinnerSize: 19,
    raised: false,
    stroked: false,
    flat: true,
    fab: false,
    buttonColor: "accent",
    spinnerColor: "accent",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-qrcode",
      inline: true,
    },
  };

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<PropertyDetailDocumentPrintModuleDialogComponent>,
    public cancelContractDialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private configService: ConfigService,
    private propertiesService: PropertiesService,
    private translateService: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    private dialog: MatDialog,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private usersService: UsersService,
    private ddtsService: DdtsService
  ) {}

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    console.log(this.data)
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = res)
      )
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    this.dialogIsLoading = false;
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Handle print file qrCode
   *
   * @since 1.0.0
   */
  onPrint(target: string): void {
    const qrCodes = [];
    let barCode = this.data.itemData.barCode;
    if (typeof barCode === "undefined" || barCode === "") {
      this.toastrService.error("No qrCodes to inject");
      return;
    }

    barCode = barCode.toUpperCase();

    if (barCode.slice(-1) === "C") {
      this.subscriptions.push(
        this.ddtsService.getDdtdocumentByCode(barCode).subscribe(
          (res) => {
            if (res.status) {
              if (
                res.data &&
                res.data.files &&
                Array.isArray(res.data.files)
              ) {
                for (const file of res.data.files) {
                  if (file && file.name) {
                    qrCodes.push(file.name);
                  }
                }
              }
              // Fallback: paper document without usable DDT file names -> encode the barCode (same as digital docs)
              if (!qrCodes.length) {
                qrCodes.push(barCode);
              }
              let data;

              if (target === "qr") {
                data = {
                  target,
                  qrCodes,
                  propertyName: this.data.property.name,
                  propertyCity: this.data.property.address.city,
                  propertyAddress: `${this.data.property.address.route}, ${this.data.property.address.streetNumber}`,
                  area: this.data.area,
                  docs: this.data.docs,
                };
              } else if (target === "module") {
                data = {
                  target,
                  qrCodes,
                  propertyName: this.data.property.name,
                  propertyCity: this.data.property.address.city,
                  propertyAddress: `${this.data.property.address.route}, ${this.data.property.address.streetNumber}`,
                  area: this.data.area,
                  uploadDate: this.data.itemData.createdAt,
                  content: this.data.itemData.contenutoDocumento,
                  docs: this.data.docs,
                  structures: this.data.structure,
                };
              } else {
                this.toastrService.error("missing print target");
              }

              console.log("data", data)
              // Open dialog
              this.dialog.open(PrintQrDialogComponent, {
                data,
                width: "600px",
              });
            } else {
              // Non c'è il DDT perchè è un documento importato dlala 1
              qrCodes.push(barCode);

              let data;

              if (target === "qr") {
                data = {
                  target,
                  qrCodes,
                  propertyName: this.data.property.name,
                  propertyCity: this.data.property.address.city,
                  propertyAddress: `${this.data.property.address.route}, ${this.data.property.address.streetNumber}`,
                  area: this.data.area,
                  docs: this.data.docs,
                };
              } else if (target === "module") {
                data = {
                  target,
                  qrCodes,
                  propertyName: this.data.property.name,
                  propertyCity: this.data.property.address.city,
                  propertyAddress: `${this.data.property.address.route}, ${this.data.property.address.streetNumber}`,
                  area: this.data.area,
                  uploadDate: this.data.itemData.createdAt,
                  content: this.data.itemData.contenutoDocumento,
                  docs: this.data.docs,
                  structures: this.data.structure,
                };
              } else {
                this.toastrService.error("missing print target");
              }

              // Open dialog
              this.dialog.open(PrintQrDialogComponent, {
                data,
                width: "600px",
              });
            }
          },
          (err) => {
            // if we came here users with given userId do not exist
            this.helperService.handleError(err);
          }
        )
      );
    } else {
      qrCodes.push(barCode);

      let data;

      if (target === "qr") {
        data = {
          target,
          qrCodes,
          propertyName: this.data.property.name,
          propertyCity: this.data.property.address.city,
          propertyAddress: `${this.data.property.address.route}, ${this.data.property.address.streetNumber}`,
          area: this.data.area,
          docs: this.data.docs,
        };
      } else if (target === "module") {
        data = {
          target,
          qrCodes,
          propertyName: this.data.property.name,
          propertyCity: this.data.property.address.city,
          propertyAddress: `${this.data.property.address.route}, ${this.data.property.address.streetNumber}`,
          area: this.data.area,
          uploadDate: this.data.itemData.createdAt,
          content: this.data.itemData.contenutoDocumento,
          docs: this.data.docs,
          structures: this.data.structure,
        };
      } else {
        this.toastrService.error("missing print target");
      }

      // Open dialog
      this.dialog.open(PrintQrDialogComponent, {
        data,
        width: "600px",
      });
    }
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   */
  onClose(data?: any): void {
    this.dialogRef.close(data);
  }
}
